import { __awaiter } from "tslib";
import { LOGIN_SCOPES } from '../../../constants';
import { clearCombinedToken as clearAccessToken } from '../../../store/sessionStorageHelpers/accessTokenHelper/accessTokenHelper';
import { FAKE_LOCAL_BMS_INSTANCE_CODE, setAvailableEvoInstances, } from '../../../store/sessionStorageHelpers/entitlementsHelper/entitlementsHelper';
import { BentleyEntitlementsApiClient } from '../BentleyEntitlementsApiClient';
import { FAKE_LOCAL_BMS_LICENSE_ACCESS } from './fakeLocalBmsLicenceAccess';
function buildServiceParams(service) {
    return (Array.isArray(service) ? service : [service])
        .map((s) => `service=${encodeURIComponent(s)}`)
        .join('&');
}
function matchEvoInstances(discovery, service) {
    const hubs = new Map(discovery.hubs.map((h) => [h.code, h]));
    const orgs = new Map(discovery.organizations.map((o) => [o.id, o]));
    const services = Array.isArray(service) ? service : [service];
    return discovery.service_access
        .filter((sa) => services.some((s) => sa.services.includes(s)))
        .map((sa) => ({ hub: hubs.get(sa.hub_code), org: orgs.get(sa.org_id) }))
        .filter((inst) => !!inst.hub && !!inst.org);
}
function handleDiscoveryError(error) {
    const { status } = error;
    if (status && typeof status === 'number' && status >= 400) {
        clearAccessToken();
    }
    return { error };
}
export const BentleyEntitlementsApi = BentleyEntitlementsApiClient.injectEndpoints({
    endpoints: (build) => ({
        loadBentleyLicenseOrgs: build.query({
            queryFn(_a, _queryApi_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ service, combineResponses }, _queryApi, _extraOptions, baseQuery) {
                    var _b;
                    const serviceParams = buildServiceParams(service);
                    const discoveryResponse = yield baseQuery(Object.assign({ url: `/evo/identity/v2/discovery?${serviceParams}` }, (combineResponses && { params: { combineResponses } })));
                    if (discoveryResponse.error) {
                        return handleDiscoveryError(discoveryResponse.error);
                    }
                    const discovery = (_b = discoveryResponse.data) === null || _b === void 0 ? void 0 : _b.discovery;
                    if (!discovery) {
                        return { error: { status: 'CUSTOM_ERROR', error: 'Error: No licenses' } };
                    }
                    if (!discovery.service_access.length) {
                        setAvailableEvoInstances([]);
                        return { data: discovery };
                    }
                    const availableEvoInstances = matchEvoInstances(discovery, service);
                    setAvailableEvoInstances(availableEvoInstances);
                    if (availableEvoInstances.length === 0) {
                        return {
                            error: {
                                status: 'CUSTOM_ERROR',
                                error: `Error: No valid org/hub for ${typeof service === 'string' ? service : service.join(' ')}`,
                            },
                        };
                    }
                    return { data: discovery };
                });
            },
        }),
        loadBentleyLicenseAccess: build.query({
            queryFn(_a, _queryApi_1, _extraOptions_1, baseQuery_1) {
                return __awaiter(this, arguments, void 0, function* ({ service, hubCode, orgId, requiredScope, combineResponses, hubUrl }, _queryApi, _extraOptions, baseQuery) {
                    if (requiredScope.length === 0) {
                        return {
                            error: {
                                status: 'CUSTOM_ERROR',
                                error: 'Error: Required scopes is missing',
                            },
                        };
                    }
                    else {
                        const invalidScope = requiredScope.find((scope) => !LOGIN_SCOPES.includes(scope));
                        if (invalidScope) {
                            return {
                                error: {
                                    status: 'CUSTOM_ERROR',
                                    error: `Error: ${invalidScope} scope is invalid`,
                                },
                            };
                        }
                    }
                    if (orgId === '') {
                        return {
                            error: { status: 'CUSTOM_ERROR', error: 'Error: Org id is unprovided' },
                        };
                    }
                    if (hubCode === '') {
                        return {
                            error: { status: 'CUSTOM_ERROR', error: 'Error: Hub code is unprovided' },
                        };
                    }
                    if (hubCode === FAKE_LOCAL_BMS_INSTANCE_CODE) {
                        return {
                            data: FAKE_LOCAL_BMS_LICENSE_ACCESS(orgId, hubCode),
                        };
                    }
                    const requiredScopes = requiredScope
                        .map((scope) => `required_scope=${encodeURIComponent(scope)}`)
                        .join('&');
                    const serviceParams = buildServiceParams(service);
                    const instanceParams = `hub=${encodeURIComponent(hubCode)}&org_id=${encodeURIComponent(orgId)}`;
                    const licenseAccessResponse = yield baseQuery(Object.assign({ url: hubUrl
                            ? `${hubUrl}/workspace/evo/identity/v2/license-access?${requiredScopes}&${serviceParams}&${instanceParams}`
                            : `/evo/identity/v2/license-access?${requiredScopes}&${serviceParams}&${instanceParams}` }, (combineResponses && {
                        params: Object.assign({ combineResponses }, (hubUrl && { hubUrl })),
                    })));
                    if (licenseAccessResponse.error) {
                        return { error: licenseAccessResponse.error };
                    }
                    return licenseAccessResponse;
                });
            },
        }),
    }),
});
export const { useLoadBentleyLicenseOrgsQuery, useLazyLoadBentleyLicenseOrgsQuery, useLoadBentleyLicenseAccessQuery, } = BentleyEntitlementsApi;
//# sourceMappingURL=BentleyEntitlementsAPI.js.map